/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import './configuratorWindow.scss';
// import Draggable from 'react-draggable';
import _ from 'lodash';
import { SLTXFormatRootFileVariant } from '../fileFormats/sltx/sltx';
import { ModelRuleData } from '../modelConfiguration/rules/modelRuleData';
import { TreeOptionSelections } from '../modelConfiguration/data/optionsSelection';
import { ModelTreeNode } from '../modelConfiguration/data/modelOptionsTree';
import { TreeSelections } from '../modelConfiguration/data/treeSelection';
import { TreeOptionsSelected } from '../modelConfiguration/logic/treeOptionsSelected';
import { TreeBuilding } from '../modelConfiguration/logic/treeBuilding';
import { ModelVariants } from '../react/modelVariants/modelVariants';
import { ModelConfigurationLinear } from '../react/modelConfiguration/modelConfigurationLinear';
import { CatalogTagsTranslation } from './Data/catalogTagsTranslation';

import CloseSymbol from './Resources/close.svg';
import HideSymbol from './Resources/hide.svg';
import { ModelConfigurationLinearCustomAttributes } from '../react/modelConfiguration/modelConfigurationLinearCustomAttributes';
import { CustomAttributeValues } from '../customAttributes/customAttributeValue';

import { PriceData } from '../fileFormats/catalog/catalog';
import { SLTXFile } from '../fileFormats/sltx/sltxFile';

import { RulesEvaluationHelpers } from '../modelConfiguration/rules/rulesEvaluationHelpers';
import { CustomAttributeData } from '../customAttributes/customAttributes';
import { useAppSelector } from '../../../Redux/Store/hooks';

interface ConfiguratorWindowParameters {
  // Model info files (static data)
  sltxFile: SLTXFile;
  // Current Values of the item's custom Attibutes
  customAttributesValues: CustomAttributeValues,
  // Price files structure
  priceData: PriceData[] | undefined,
  modelRules: ModelRuleData[];
  // Available variants of the model
  variants: SLTXFormatRootFileVariant[];
  // Current set of options selected by the user
  optionsSelectionState : TreeOptionSelections,
  // Translation of option tags
  tagsTranslation : CatalogTagsTranslation[],
  // Language Selected in the parent application
  languageSelected : string,
  // The current page of the configurator
  currentPage : number,
  // Function to update the options selection state
  updateOptionsSelectionState : (selection : TreeOptionSelections) => void,
  // Function to change custom attributes values
  changeCustomAttributesHandler: (attributes: CustomAttributeValues) => void;
  // Function to adjust the path to the resources like images
  getResourcePath : (path : string) => string,
  // Function to minimize the configuration window
  minimizeHandler : (isMinimized : boolean) => void,
  // Function to change the page of the cofigurator widget
  changeConfiguratorPageHandler : (page : number) => void,
  // Function to notify the intention to  commit (commit =true) or cancel (commit = false)and  close
  // eslint-disable-next-line react/require-default-props
  onCommit? : (commit: boolean) => void,
  // Flags if the configurtion is good enough for commit and activate/disable the commit action
  // eslint-disable-next-line react/require-default-props
  canCommit?: boolean,
}

export function ConfiguratorWindow({
  sltxFile,
  customAttributesValues,
  modelRules,
  variants,
  optionsSelectionState,
  priceData,
  tagsTranslation,
  languageSelected,
  currentPage,
  updateOptionsSelectionState,
  changeCustomAttributesHandler,
  getResourcePath,
  minimizeHandler,
  changeConfiguratorPageHandler,
  onCommit = undefined,
  canCommit = false,
} : ConfiguratorWindowParameters) : JSX.Element {
  const [treeToRender, setTreeToRender] = useState<ModelTreeNode>();
  const [
    treeSelectionState,
    setTreeSelectionState,
  ] = useState<TreeSelections>([]);

  const [customAttributes, setCustomAttributes] = useState<CustomAttributeData[]>([]);

  // From the store, get the strings
  const strings = useAppSelector((state) => {
    return state.TextsReducer.strings;
  })!;

  useEffect(() => {
    const itemsAttributeData : CustomAttributeData[] = [];
    sltxFile.getRootFileContents().customAttributes?.forEach((att) => {
      const attribute = { ...att };
      const translatedName = att.translations?.find((t) => t.lang === languageSelected.toLowerCase())?.value;
      attribute.name = translatedName !== undefined ? translatedName : att.name;
      attribute.selectedLang = languageSelected.toLowerCase();
      itemsAttributeData.push(attribute);
    });

    setCustomAttributes([...itemsAttributeData]);
  }, [sltxFile, languageSelected]);

  useEffect(() => {
    if (treeToRender) {
      // When the selection changes, update the tree state
      const tempTreeSelectionState = _.cloneDeep(treeSelectionState);

      TreeOptionsSelected.applyOptionsSelectionsToTreeSelections(
        optionsSelectionState,
        tempTreeSelectionState,
        treeToRender,
        (newTreeSelectionState) => {
          // Replace the contents of the local variable
          tempTreeSelectionState.splice(0, Infinity, ...newTreeSelectionState);
        },
      );
      setTreeSelectionState(tempTreeSelectionState);
    }

  }, [optionsSelectionState]);

  useEffect(() => {
    const tempOptionsSelectionState = _.cloneDeep(optionsSelectionState);
    // Build a new tree to display
    const tree = TreeBuilding.buildTree(
      modelRules,
      sltxFile.getRootFileContents(),
      tempOptionsSelectionState,
    );
    setTreeToRender(tree);
  }, [sltxFile, modelRules, optionsSelectionState]);

  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isButtonEnabled, setIsButtonEnabled] = useState(true);

  useEffect(() => {
    const container = containerRef.current;

    const checkWidth = () => {
      if (container) {
        const minWidth = parseFloat(getComputedStyle(container).minWidth);
        setIsButtonEnabled(container.offsetWidth <= minWidth);
      }
    };

    // Initialize the check
    checkWidth();

    // Set up a ResizeObserver to monitor changes
    const resizeObserver = new ResizeObserver(checkWidth);
    if (container) {
      resizeObserver.observe(container);
    }

    // Cleanup function
    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, []);

  // ----------------------------------------------------------------------------------------------
  // Selected Options can impact some custom attribute's enable flag and consequently their value
  // we must recheck them each time the model selected options are changed..
  // ----------------------------------------------------------------------------------------------
  useEffect(() => {
    const updateStateAttributes = RulesEvaluationHelpers.evalConditions(
      RulesEvaluationHelpers.getVarValuesFromComposition(sltxFile, optionsSelectionState),
      sltxFile.getRootFileContents()?.customAttributes || [],
      customAttributesValues,
    );
    if (updateStateAttributes) changeCustomAttributesHandler([...updateStateAttributes]);
  }, [optionsSelectionState]);

  // eslint-disable-next-line max-len
  const hasCustomAttributes = (Array.isArray(customAttributes) && (customAttributes || []).length > 0);

  const increasePage = (treeBeingRendered: ModelTreeNode | undefined, page:number) => {

    if (treeBeingRendered === undefined) return;

    if (page + 1 <= treeBeingRendered.children.length) {
      if (treeBeingRendered?.children[page].singleOptionParent) {

        increasePage(treeBeingRendered, page + 1);
      } else {
        changeConfiguratorPageHandler(page + 1);
      }
    } else if (hasCustomAttributes && (page === treeBeingRendered.children.length)) {
      changeConfiguratorPageHandler(page + 1);
    }
  };
  const decreasePage = (treeBeingRendered: ModelTreeNode | undefined, page:number) => {

    if (treeBeingRendered === undefined) return;
    if (page >= 1) {
      if ((page - 1) !== 0 && treeBeingRendered?.children[page - 2].singleOptionParent) {

        decreasePage(treeBeingRendered, page - 1);
      } else {
        changeConfiguratorPageHandler(page - 1);
      }
    }
  };

  const progressIndicator = treeToRender ? `${TreeBuilding.getNumberOfMultipleBranchChidlrenTillPage(treeToRender, currentPage, hasCustomAttributes)} / ${TreeBuilding.getNumberOfMultipleBranchChidlren(treeToRender) + (hasCustomAttributes ? 1 : 0)}` : '';
  const isLastPage = treeToRender ? TreeBuilding.getNumberOfMultipleBranchChidlrenTillPage(treeToRender, currentPage, hasCustomAttributes) === (TreeBuilding.getNumberOfMultipleBranchChidlren(treeToRender) + (hasCustomAttributes ? 1 : 0)) : true;
  if (!treeToRender) {
    return <div />;
  }
  // );
  return (
    <div ref={containerRef} className="object-configuration-window">
      <div className="container" style={{ maxHeight: '100%' }}>
        {(() => {
          // ----------------------------------------------------------------
          // Derived values
          // ----------------------------------------------------------------
          const childrenCount = treeToRender?.children?.length ?? 0;
          const customAttrsPageIndex = hasCustomAttributes ? childrenCount + 1 : null;
          const isVariantsPage = currentPage === 0;
          const isCustomAttrsPage = customAttrsPageIndex !== null && currentPage === customAttrsPageIndex;

          // ------------------------------------------------------------------
          // To avoid repeat this in all branches...or create a component
          // ------------------------------------------------------------------
          // eslint-disable-next-line react/no-unstable-nested-components
          function CloseRow() {
            return (
              <div className="row justify-content-md-center">
                <button
                  type="button"
                  className="btn btn-light hide-btn"
                  style={{ backgroundColor: 'darkgray' }}
                  onClick={() => { minimizeHandler(true); }}
                  disabled={false}
                >
                  <img style={{ padding: 0, margin: 0 }} src={HideSymbol} alt="Hide" />
                </button>
                { onCommit ? (
                  <button
                    type="button"
                    className="btn btn-light close-btn"
                    style={{ backgroundColor: 'darkgray' }}
                    onClick={() => { if (onCommit) onCommit(false); }}
                    disabled={false}
                  >
                    <img style={{ padding: 0, margin: 0 }} src={CloseSymbol} alt="Close" />
                  </button>
                ) : (
                  <div />
                )}
              </div>
            );
          }

          // ----------------------------------------------------------------
          // Variants page, the first if any to show...
          // ----------------------------------------------------------------
          if (isVariantsPage) {
            return (
              <>
                <CloseRow />
                <div className="row justify-content-md-center">
                  <p className="section-title">Variants</p>
                </div>
                <div className="row justify-content-md-center">
                  <ModelVariants
                    variants={variants}
                    optionsSelectionState={optionsSelectionState}
                    tagsTranslation={tagsTranslation}
                    languageSelected={languageSelected}
                    updateOptionsSelectionState={updateOptionsSelectionState}
                    getResourcePath={getResourcePath}
                  />
                </div>
              </>
            );
          }

          // ----------------------------------------------------------------
          // Model's custom attributes pages
          // ----------------------------------------------------------------
          if (isCustomAttrsPage) {
            return (
              <>
                <CloseRow />
                <ModelConfigurationLinearCustomAttributes
                  sltxFile={sltxFile}
                  selectedOptions={optionsSelectionState}
                  customAttributes={customAttributes}
                  customAttributesValues={customAttributesValues}
                  priceData={priceData}
                  languageSelected={languageSelected}
                  changeCustomAttributesHandler={changeCustomAttributesHandler}
                  bCheckForExtras={true}
                />
              </>
            );
          }
          // ----------------------------------------------------------------
          // configuration pages (1..non single option childrenCount)
          // ----------------------------------------------------------------
          return (
            <>
              <CloseRow />
              <ModelConfigurationLinear
                sltxFile={sltxFile.getRootFileContents()}
                rules={modelRules}
                optionsSelectionState={optionsSelectionState}
                tagsTranslation={tagsTranslation}
                languageSelected={languageSelected}
                updateOptionsSelectionState={updateOptionsSelectionState}
                getResourcePath={getResourcePath}
                nodeIndex={currentPage - 1}
              />
            </>
          );
        })()}

        {/* footer nav */}
        <div
          className="row justify-content-md-center"
          style={{ marginTop: '10px', marginBottom: '10px' }}
        >
          {(() => {
            const childrenCount = treeToRender?.children?.length ?? 0;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const pageCount = 1 // variants page (index 0)
            + childrenCount
            + (hasCustomAttributes ? 1 : 0);

            return (
              <>
                <div className="col justify-content-md-left">
                  {currentPage > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { decreasePage(treeToRender, currentPage); }}
                  >
                    {'<'}
                  </button>
                  )}
                </div>

                <div className="col justify-content-md-center">
                  <h3 style={{ textAlign: 'center' }}>{progressIndicator}</h3>
                </div>

                <div className="col justify-content-md-right">
                  {treeToRender && !isLastPage && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ float: 'right' }}
                    onClick={() => { increasePage(treeToRender, currentPage); }}
                  >
                    {'>'}
                  </button>
                  )}
                  {treeToRender && onCommit && isLastPage && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ float: 'right' }}
                    onClick={() => { onCommit(true); }}
                    disabled={!canCommit}
                  >
                    {strings.CONFIRM_ACTION}
                  </button>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );

}
